import { useState, useRef } from 'react';
import {
  Box,
  MenuItem,
  Typography,
  Paper,
  ClickAwayListener,
  MenuList,
  Popper,
  styled,
} from '@mui/material';
import { ChevronRightOutlined, ChevronLeftOutlined } from '@mui/icons-material';

import { getThemeColor } from '../../utils/common';
import { Themes } from '../../constants';
import { FORTA_WHITE, FORTA_BLACK } from '../../utils/colors';

import type { PopperProps as MPopperProps } from '@mui/material';
import type { ReactNode } from 'react';

interface SubmenuProps {
  colorTheme?: Themes;
  dataTestId?: string;
  label?: string;
  disabled?: boolean;
  children?: ReactNode;
  align?: 'left' | 'right';
}

interface PopperProps extends MPopperProps {
  colorTheme?: Themes;
  align?: 'left' | 'right';
}

const StyledPopper = styled(
  ({ colorTheme: _colorTheme, ...props }: PopperProps) => (
    <Popper nonce={undefined} onResize={undefined} onResizeCapture={undefined} {...props} />
  ),
  { shouldForwardProp: (prop) => prop !== 'colorTheme' },
)<PopperProps>(({ colorTheme }) => ({
  '& .MuiPaper-root': {
    width: 'auto',
    marginTop: 5,
    backgroundColor: getThemeColor(colorTheme).light3,
    color: FORTA_BLACK,
    fontSize: '1em',
    borderRadius: '10px',
    flexGrow: 1,
    boxShadow: `0px 4px 4px ${getThemeColor(colorTheme).light2}`,
    '& .MuiMenuItem-root': {
      padding: '0.4em 1em',
      '& .MuiSvgIcon-root': {
        color: FORTA_BLACK,
        fontSize: '1.2em',
        marginTop: '0.2em',
      },
      svg: {
        fill: FORTA_BLACK,
      },
    },
  },
  '& .MuiMenuItem-root:hover': {
    backgroundColor: getThemeColor(colorTheme).primary,
    color: FORTA_WHITE,
    '& .MuiBox-root': {
      '& > svg': {
        fill: FORTA_WHITE,
      },
    },
  },
  '& .MuiMenuItem-root.Mui-selected': {
    backgroundColor: getThemeColor(colorTheme).primary,
    color: FORTA_WHITE,
  },
  '& .MuiMenuItem-root.Mui-disabled': {
    opacity: 1,
    '& *:not(:last-child)': {
      opacity: 0.3,
    },
    '&:last-child': {
      opacity: 1,
    },
  },
  '& .MuiMenuItem-root.Mui-selected:hover': {
    backgroundColor: getThemeColor(colorTheme).primary,
  },
}));

function FSubmenu(props: SubmenuProps) {
  const {
    label,
    children,
    disabled = false,
    align = 'left',
    colorTheme = Themes.Primary,
    dataTestId,
  } = props;

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const submenuActive = useRef(false);

  const open = Boolean(anchorEl);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (close = false) => {
    if (!submenuActive.current || close) {
      setAnchorEl(null);
    }
  };

  const handleAsyncClose = () => {
    setTimeout(handleClose, 100);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', width: 'auto' }}>
      <MenuItem
        data-testid={dataTestId}
        onMouseEnter={handleOpen}
        onMouseLeave={handleAsyncClose}
        disabled={disabled}
        sx={{
          padding: `0.4em ${align === 'left' ? '2em' : '0'} 0.4em ${
            align === 'left' ? '0' : '2em'
          } !important`,
          backgroundColor: open
            ? getThemeColor(colorTheme).light1
            : getThemeColor(colorTheme).light3,
          color: open ? FORTA_WHITE : FORTA_BLACK,
        }}
      >
        {align === 'left' && <ChevronLeftOutlined />}
        <Typography m={0.5}>{label ? label : ''}</Typography>
        {align === 'right' && <ChevronRightOutlined />}
      </MenuItem>
      <StyledPopper
        open={open}
        anchorEl={anchorEl}
        placement={'right-start'}
        transition
        disablePortal
        colorTheme={colorTheme}
        onMouseEnter={() => (submenuActive.current = true)}
        onMouseLeave={() => {
          submenuActive.current = false;
          handleClose(true);
        }}
        style={{
          top: 'auto',
          left: 'auto',
          transform:
            align === 'left' ? `translateX(-100%)` : `translateX(${anchorEl?.offsetWidth}px)`,
        }}
      >
        <Paper>
          <ClickAwayListener onClickAway={() => handleClose(false)}>
            <MenuList autoFocusItem={open}>{children}</MenuList>
          </ClickAwayListener>
        </Paper>
      </StyledPopper>
    </Box>
  );
}

export default FSubmenu;
